import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Mail, Globe } from "lucide-react";

const SUPPORT_EMAIL = "support@vastavik.org";

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-300">
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-16">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white mb-6 transition-colors">
          <ChevronLeft size={20} /> Back
        </button>
        <h1 className="text-2xl font-semibold mb-6 font-['Caviar_Dreams']">{title}</h1>
        <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed font-['Product_Sans']">{children}</div>
      </div>
    </div>
  );
}

export function HelpSupport() {
  return (
    <Shell title="Help & Support">
      <p>Need a hand? We're here to help with uploads, verification, billing and your account.</p>
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
      >
        <Mail size={20} className="text-blue-500 dark:text-blue-400" />
        <div>
          <div className="font-medium text-black dark:text-white">Email Support</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{SUPPORT_EMAIL}</div>
        </div>
      </a>
      <a
        href="https://vastavik.org"
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
      >
        <Globe size={20} className="text-blue-500 dark:text-blue-400" />
        <div>
          <div className="font-medium text-black dark:text-white">Visit vastavik.org</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Guides, FAQs and updates</div>
        </div>
      </a>
    </Shell>
  );
}

export function About() {
  return (
    <Shell title="About Vastavik">
      <p>
        Vastavik is a media authenticity registry. Protect your photos, videos and audio with a
        blockchain-verified Vastavik ID, so anyone can confirm they're genuine and yours.
      </p>
      <p className="text-gray-600 dark:text-gray-400">
        Every upload is checked for authenticity, given a unique Vastavik ID, and registered on a
        private blockchain — creating a tamper-proof record of origin.
      </p>
      <div className="text-sm text-gray-500 dark:text-gray-500 pt-4">© {new Date().getFullYear()} Vastavik</div>
    </Shell>
  );
}

export function TermsPrivacy() {
  return (
    <Shell title="Terms & Privacy">
      <p className="text-gray-600 dark:text-gray-400">
        By using Vastavik you agree to our Terms of Service and Privacy Policy. We process your media
        to verify and protect it, and store only what's needed to provide the service.
      </p>
      <a
        href="https://vastavik.org/terms"
        target="_blank"
        rel="noreferrer"
        className="block p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 font-medium text-black dark:text-white transition-colors"
      >
        Terms of Service →
      </a>
      <a
        href="https://vastavik.org/privacy"
        target="_blank"
        rel="noreferrer"
        className="block p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 font-medium text-black dark:text-white transition-colors"
      >
        Privacy Policy →
      </a>
    </Shell>
  );
}